import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { X, FilterX } from 'lucide-react';
import { treatmentTypes, specializations, insuranceProviders, mockStates } from '../data/mock';

const SearchFilters = ({ 
  filters, 
  onFiltersChange, 
  onClearFilters,
  className = '' 
}) => {
  const handleStateChange = (state) => {
    onFiltersChange({
      ...filters,
      state: state === 'all' ? '' : state
    });
  };

  const handleTreatmentTypeChange = (type, checked) => {
    const updated = checked
      ? [...(filters.treatmentTypes || []), type]
      : (filters.treatmentTypes || []).filter(t => t !== type);
    
    onFiltersChange({
      ...filters,
      treatmentTypes: updated
    });
  };

  const handleSpecializationChange = (spec, checked) => {
    const updated = checked
      ? [...(filters.specializations || []), spec]
      : (filters.specializations || []).filter(s => s !== spec);
    
    onFiltersChange({
      ...filters,
      specializations: updated
    });
  };

  const handleInsuranceChange = (ins, checked) => {
    const updated = checked
      ? [...(filters.insurance || []), ins]
      : (filters.insurance || []).filter(i => i !== ins);
    
    onFiltersChange({
      ...filters,
      insurance: updated
    });
  };

  const getActiveFiltersCount = () => {
    return [
      filters.state ? 1 : 0,
      (filters.treatmentTypes || []).length,
      (filters.specializations || []).length,
      (filters.insurance || []).length
    ].reduce((a, b) => a + b, 0);
  };

  const activeCount = getActiveFiltersCount();

  return (
    <Card className={`sticky top-24 ${className}`}>
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Filters</CardTitle>
          {activeCount > 0 && (
            <div className="flex items-center space-x-2">
              <Badge variant="secondary">{activeCount} active</Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-red-600 hover:text-red-700"
              >
                <FilterX className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* State Filter */}
        <div>
          <Label className="text-sm font-medium mb-3 block">State</Label>
          <Select value={filters.state || 'all'} onValueChange={handleStateChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All States</SelectItem>
              {mockStates.map((state) => (
                <SelectItem key={state.code} value={state.code}>
                  {state.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Treatment Types */}
        <div>
          <Label className="text-sm font-medium mb-3 block">Treatment Types</Label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {treatmentTypes.map((type) => (
              <div key={type} className="flex items-center space-x-2">
                <Checkbox
                  id={`treatment-${type}`}
                  checked={(filters.treatmentTypes || []).includes(type)}
                  onCheckedChange={(checked) => handleTreatmentTypeChange(type, checked)}
                />
                <Label htmlFor={`treatment-${type}`} className="text-sm cursor-pointer">
                  {type}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Specializations */}
        <div>
          <Label className="text-sm font-medium mb-3 block">Specializations</Label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {specializations.map((spec) => (
              <div key={spec} className="flex items-center space-x-2">
                <Checkbox
                  id={`spec-${spec}`}
                  checked={(filters.specializations || []).includes(spec)}
                  onCheckedChange={(checked) => handleSpecializationChange(spec, checked)}
                />
                <Label htmlFor={`spec-${spec}`} className="text-sm cursor-pointer">
                  {spec}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Insurance */}
        <div>
          <Label className="text-sm font-medium mb-3 block">Insurance Accepted</Label>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {insuranceProviders.map((ins) => (
              <div key={ins} className="flex items-center space-x-2">
                <Checkbox
                  id={`insurance-${ins}`}
                  checked={(filters.insurance || []).includes(ins)}
                  onCheckedChange={(checked) => handleInsuranceChange(ins, checked)}
                />
                <Label htmlFor={`insurance-${ins}`} className="text-sm cursor-pointer">
                  {ins}
                </Label>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SearchFilters;